import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Trash2, Check } from "lucide-react";
import { API, BOOKING_SERVICES } from "../data/site";

export const CLIENT_STATUSES = ["lead", "follow_up", "customer", "not_interested"];
export const CLIENT_STATUS_LABELS = { lead: "Lead", follow_up: "Follow Up", customer: "Customer", not_interested: "Not Interested" };

const inputCls = "w-full rounded-xl border border-forest/20 bg-white px-4 py-3 text-sm text-ink outline-none transition-colors focus:border-forest focus:ring-2 focus:ring-forest/15";
const EMPTY = { name: "", address: "", phone: "", email: "", services: [], status: "lead", notes: "" };

export const ClientModal = ({ client, token, onClose, onSaved, onDeleted }) => {
    const isNew = !client;
    const [form, setForm] = useState(client ? { ...EMPTY, ...client } : EMPTY);
    const [saving, setSaving] = useState(false);
    const headers = { Authorization: `Bearer ${token}` };
    const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

    const toggleService = (s) =>
        setForm((f) => ({ ...f, services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s] }));

    const save = async () => {
        if (!form.name.trim()) { toast.error("Name is required."); return; }
        setSaving(true);
        try {
            const { data } = isNew
                ? await axios.post(`${API}/clients`, form, { headers })
                : await axios.patch(`${API}/clients/${client.id}`, form, { headers });
            toast.success(isNew ? "Client added." : "Client updated.");
            onSaved(data);
        } catch {
            toast.error("Could not save the client.");
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!window.confirm("Delete this client permanently?")) return;
        try {
            await axios.delete(`${API}/clients/${client.id}`, { headers });
            toast.success("Client deleted.");
            onDeleted(client.id);
        } catch {
            toast.error("Could not delete the client.");
        }
    };

    return (
        <div data-testid="client-modal" className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-forest-night/60 p-4 backdrop-blur-sm">
            <div className="my-8 w-full max-w-lg rounded-3xl bg-bone-card p-6 shadow-2xl sm:p-8">
                <div className="flex items-center justify-between">
                    <h2 className="font-display text-2xl font-bold tracking-tight text-ink">{isNew ? "Add Client" : "Edit Client"}</h2>
                    <button onClick={onClose} data-testid="client-modal-close" aria-label="Close"
                        className="flex h-9 w-9 items-center justify-center rounded-full border border-forest/15 text-forest hover:bg-bone-subtle">
                        <X size={16} />
                    </button>
                </div>
                <div className="mt-6 space-y-4">
                    <div>
                        <label className="eyebrow mb-1.5 block text-forest">Name *</label>
                        <input data-testid="client-form-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="Client name" />
                    </div>
                    <div>
                        <label className="eyebrow mb-1.5 block text-forest">Address</label>
                        <input data-testid="client-form-address" className={inputCls} value={form.address} onChange={set("address")} placeholder="Street address, suburb" />
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                            <label className="eyebrow mb-1.5 block text-forest">Phone</label>
                            <input data-testid="client-form-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="04xx xxx xxx" />
                        </div>
                        <div>
                            <label className="eyebrow mb-1.5 block text-forest">Email</label>
                            <input data-testid="client-form-email" className={inputCls} value={form.email} onChange={set("email")} placeholder="Optional" />
                        </div>
                    </div>
                    <div>
                        <p className="eyebrow mb-2 text-forest">Interested in</p>
                        <div className="flex flex-wrap gap-2">
                            {BOOKING_SERVICES.map((s) => {
                                const active = form.services.includes(s);
                                return (
                                    <button type="button" key={s} onClick={() => toggleService(s)} data-testid={`client-form-service-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                        className={`flex items-center gap-1.5 rounded-full border px-4 py-2 text-xs font-bold transition-colors ${active ? "border-forest bg-forest text-bone" : "border-forest/20 bg-white text-ink hover:border-forest/50"}`}>
                                        {active && <Check size={12} />} {s}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <div>
                        <label className="eyebrow mb-1.5 block text-forest">Status</label>
                        <select data-testid="client-form-status" className={inputCls} value={form.status} onChange={set("status")}>
                            {CLIENT_STATUSES.map((s) => <option key={s} value={s}>{CLIENT_STATUS_LABELS[s]}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="eyebrow mb-1.5 block text-forest">Notes</label>
                        <textarea data-testid="client-form-notes" rows={3} className={inputCls} value={form.notes} onChange={set("notes")}
                            placeholder="Where you met them, what they said, best time to follow up..." />
                    </div>
                </div>
                <div className="mt-7 flex flex-wrap items-center gap-3">
                    <button onClick={save} disabled={saving} data-testid="client-form-save"
                        className="rounded-full bg-forest px-7 py-3 text-sm font-bold text-bone transition-colors hover:bg-forest-deep disabled:opacity-60">
                        {saving ? "Saving..." : isNew ? "Add Client" : "Save Changes"}
                    </button>
                    {!isNew && (
                        <button onClick={remove} data-testid="client-delete-button"
                            className="ml-auto flex items-center gap-2 rounded-full border border-clay/40 px-5 py-3 text-sm font-bold text-clay hover:bg-clay/10">
                            <Trash2 size={14} /> Delete
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};
